import React from 'react'

import { TableHead } from '../../../../../../components/pdf/Table'
import Grid from '../../../../../../components/pdf/Grid'
import Text from '../../../../../../components/pdf/Text'
import View from '../../../../../../components/pdf/View'
import { MATRIZ_RISCO, PROBABILIDADE_REFERENCIA, SEVERIDADE_REFERENCIA } from '../../_helpers/riskEngine'
import { RISCO_CELL_COLORS, TABLE_ACCENT_DARK_RED, TABLE_BORDER_RED } from '../../_helpers/brand'
import { RedDataCell, RedHeadCell, RedRow, RedTable } from './BradescoTable'

// Célula "rótulo ... número" na mesma linha (ex.: "Muito Baixo" à esquerda, "1" à
// direita) — o formato usado no modelo para o rótulo de cada linha de Probabilidade,
// diferente do formato empilhado (rótulo/número em duas linhas) usado no cabeçalho de
// Severidade e nas células de resultado.
const SplitLabelCell = ({ label, numero }) => (
  <TableHead color={TABLE_BORDER_RED} bgColor="#FFFFFF">
    <Grid justify="space-between">
      <Text bold size="xsmall">
        {label}
      </Text>
      <Text bold size="xsmall">
        {numero}
      </Text>
    </Grid>
  </TableHead>
)

/**
 * Tabela "Matriz de Risco - Classificação de Nível de Risco Ocupacional" (Probabilidade
 * x Severidade) da subseção 9.3 (ver `09.ClassificacaoRiscosFisicoQuimicoBiologicoSection.js`),
 * reaproveitando a mesma matriz 5x5 usada no cálculo dinâmico do Inventário de Riscos
 * (Seção 18). Extraída para um componente próprio porque este markup também tinha sido
 * duplicado, sob o título incorreto "12. MATRIZ DE RISCO...", no arquivo que se chamava
 * `12.MatrizRiscoSection.js` — removido (ver `PdfDocument.js`); a Matriz de Risco
 * psicossocial 4x5 da Seção 10 real (10.3) é uma tabela distinta e estática, não usa este
 * componente (ver `10.ClassificacaoRiscosErgonomicoPsicossocialSection.js`).
 *
 * Layout/cores redesenhados para bater com o modelo real (confirmado por inspeção
 * pixel-a-pixel, não presumido): cabeçalho "Severidade" em barra vermelha escura
 * (colSpan 5), rótulo "Probabilidade" em coluna vermelha escura estreita (rowSpan 5,
 * o texto simplesmente quebra dentro da coluna estreita — não há rotação de texto no
 * modelo), e cada célula 5x5 pintada por classificação (heat-map: Trivial=azul,
 * Baixo=verde, Moderado=amarelo, Alto=laranja, Crítico=vermelho — `RISCO_CELL_COLORS`).
 * Nenhum valor/ID calculado é alterado — só a cor de fundo de cada célula, derivada do
 * mesmo `classificacao` já retornado por `MATRIZ_RISCO` (`_helpers/riskEngine.js`).
 */
const MatrizRiscoTable = () => (
  <View style={{ overflowX: 'auto' }}>
    <RedTable width="100%" fixedLayout>
      <RedRow>
        <RedHeadCell colSpan={2} rowSpan={2} bgColor="#FFFFFF" />
        <RedHeadCell colSpan={5} bgColor={TABLE_ACCENT_DARK_RED}>
          Severidade
        </RedHeadCell>
      </RedRow>
      <RedRow>
        {SEVERIDADE_REFERENCIA.map(({ ID, descricao }) => (
          <RedHeadCell key={ID} bgColor="#FFFFFF" size="xsmall">
            {descricao}
            <br />
            {ID}
          </RedHeadCell>
        ))}
      </RedRow>

      {MATRIZ_RISCO.map((linha, index) => {
        const probabilidade = PROBABILIDADE_REFERENCIA.find(p => p.ID === linha.probabilidadeID)
        return (
          <RedRow key={linha.probabilidadeID}>
            {index === 0 && (
              <RedHeadCell rowSpan={PROBABILIDADE_REFERENCIA.length} width="24px" bgColor={TABLE_ACCENT_DARK_RED}>
                Probabilidade
              </RedHeadCell>
            )}
            <SplitLabelCell label={probabilidade?.descricao} numero={probabilidade?.ID} />
            {linha.severidades.map(celula => (
              <RedDataCell key={celula.severidadeID} align="center" bold bgColor={RISCO_CELL_COLORS[celula.classificacao]}>
                {celula.classificacao}
                <br />
                {celula.resultado}
              </RedDataCell>
            ))}
          </RedRow>
        )
      })}
    </RedTable>
  </View>
)

export default MatrizRiscoTable
